import wx from 'weixin-js-sdk';
import has from '@atomicservice/ascf-web-sdk';
import { auth, coupon, marketing } from '@triplabo/web/stores';
import { auth as authService } from '@triplabo/web/services';
import { SHARE_DIALOG_SHOWN } from '@triplabo/web/stores/modules/auth';
import FirebaseAuth from '@triplabo/web/vendors/firebase';
import { showLoading, hideLoading } from '@triplabo/web/components/ShowLoading';
import { isWechat, isWeb, isHarmony } from './env';

const isDev = import.meta.env.VITE_APP_WEB_ENV === 'development';

let firebaseAuth: FirebaseAuth | null = null;

// 元服务(ASCF) 通过官方 web-view SDK 实时拉起华为帐号登录，拿到登录凭证 code
const requestAscfAuthorizationCode = () =>
  new Promise<string | null>((resolve, reject) => {
    has.login({
      success: res => resolve(res?.code ?? null),
      fail: error => reject(error)
    });
  });

// 检查token
const checkToken = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const refreshToken = searchParams.get('refreshToken');
  const accessToken = searchParams.get('accessToken');
  const signature = searchParams.get('signature');

  if (refreshToken) {
    auth.setRefreshToken(refreshToken);
  }
  if (accessToken) {
    auth.setAccessToken(accessToken);
  }
  if (signature) {
    auth.setSignature(signature);
  }
};

// 用华为授权码换取本系统token(harmony APP & harmony ASCF)
const loginWithHuaweiCode = async (authorizationCode: string) => {
  showLoading();
  try {
    const inviteId = coupon?.getInviteId || undefined;
    const loginBody = {
      huawei: { authorizationCode },
      couponId: inviteId
    };

    const result = await authService.login({ body: loginBody });
    if (result?.accessToken) {
      auth.setAccessToken(result?.accessToken);
      auth.setRefreshToken(result?.refreshToken);
      if (inviteId) {
        coupon.clearInviteId();
      }
      location.replace('/home');
    }
  } finally {
    hideLoading();
  }
};

// ASCF元服务登录处理
const handleAscfLogin = async () => {
  try {
    const authorizationCode = await requestAscfAuthorizationCode();
    if (!authorizationCode) {
      return;
    }
    await loginWithHuaweiCode(authorizationCode);
  } catch (error) {
    console.error('ASCF login error:', error);
  }
};

// 登录处理 harmony APP | harmony ASCF | WECHAT | WEB
const handleLogin = async () => {
  auth.clearTokens();
  sessionStorage.removeItem(SHARE_DIALOG_SHOWN);
  if (isHarmony) {
    await handleAscfLogin();
  } else if (isWechat) {
    wx.miniProgram.navigateTo({
      url: `/pages/loginPage/index?url=${window.location.href}`
    });
  } else if (isWeb) {
    location.replace('/login');
  }
};

const LoginTest = async () => {
  if (isDev && !isWechat) {
    await handleLoginTest();
    return;
  }
};

// 登录测试
const handleLoginTest = async () => {
  const loginReq = {
    body: {
      wechat: {
        loginCode: 'o1Mns6zeQ7lx_1vzI38uZm0JfCuM', // 'paymentpending'
        source: 'admin2025admin' // 'test'
      }
    }
  };
  const result = await authService.login(loginReq);
  auth.setAccessToken(result?.accessToken || '');
  auth.setRefreshToken(result?.refreshToken || '');
};

// firebase登录通用处理
const handleFirebaseLogin = async (idToken: string) => {
  try {
    const inviteId = coupon.getInviteId;
    const couponId = coupon.getCouponId;
    const affiliateId = marketing.affiliateId;
    const loginBody: {
      web: {
        idToken: string;
        affiliateId?: string;
      };
      couponId?: string;
    } = {
      web: {
        idToken,
        affiliateId
      }
    };

    if (inviteId) {
      loginBody.couponId = inviteId;
    }

    const loginResult = await authService.login({
      body: loginBody
    });
    if (loginResult) {
      auth.setAccessToken(loginResult?.accessToken || '');
      auth.setRefreshToken(loginResult?.refreshToken || '');
      if (inviteId) {
        coupon.clearInviteId();
      }
      if (couponId) {
        location.replace(
          `/coupons/redeem?couponId=${encodeURIComponent(couponId)}`
        );
      } else {
        location.replace('/home');
      }
    }
  } catch (error) {
    console.error('Firebase login error:', error);
  }
};

const getFirebaseInstance = () => {
  if (firebaseAuth) {
    return firebaseAuth;
  }

  firebaseAuth = new FirebaseAuth();
  return firebaseAuth;
};

const handleEmailPasswordLogin = async (email: string, password: string) => {
  try {
    const firebaseAuth = getFirebaseInstance();
    const res = await firebaseAuth.login(email, password);
    if (res) {
      const token = await res?.user?.getIdToken();
      await handleFirebaseLogin(token);
    }
  } catch (error) {
    console.error('Login error:', error);
  }
};

// firebase google登录
const handleGoogleLogin = async () => {
  try {
    const firebaseAuth = getFirebaseInstance();
    const res = await firebaseAuth.googleLogin();
    if (res) {
      const token = await res?.user?.getIdToken();
      await handleFirebaseLogin(token);
    }
  } catch (error) {
    console.error('Google login error:', error);
  }
};

// 新用户注册后自动登录
const handleUserRegistrationAndLogin = async (
  email: string,
  password: string
) => {
  try {
    const firebaseAuth = getFirebaseInstance();
    const registerResult = await firebaseAuth.register(email, password);
    if (registerResult) {
      await handleEmailPasswordLogin(email, password);
    }
  } catch (error) {
    console.error('Registration and login error:', error);
  }
};

const firebaseLogout = async () => {
  try {
    const firebaseAuth = getFirebaseInstance();
    await firebaseAuth.logout();
    marketing.clearMarketingParams();
    sessionStorage.removeItem(SHARE_DIALOG_SHOWN);
  } catch (error) {
    console.error('Firebase logout error:', error);
  }
};

export {
  checkToken,
  handleLogin,
  handleLoginTest,
  getFirebaseInstance,
  firebaseLogout,
  LoginTest,
  handleEmailPasswordLogin,
  handleGoogleLogin,
  handleUserRegistrationAndLogin
};
